/**
 * `attach-secrets` wizard step renderer (design §8) — vault REFERENCES only.
 *
 * HARD GUARD (P5-2, §12): this step consumes exactly two list endpoints —
 * `GET /api/secrets/vaults` (summaries: names/descriptions, no entries) and
 * `GET /api/secrets/vaults/:id` (entries as `{key, updatedAt}` only).
 * `POST /secrets/:id/read` is NEVER called from this app and no value field
 * — masked or otherwise — exists here. Secret values do not transit the
 * browser; the agent reads them only at run time (trust note below).
 *
 * Contributed by the coder feature; picked up by the P2 glob step registry
 * with ZERO edits to P2 files. Optional per the seed: Continue stays
 * enabled; empty selection is valid (D3).
 */
import { useMemo, useState } from 'react';
import { Lock } from 'lucide-react';

import { QueryErrorView } from '@/components/common/QueryErrorView';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { AgentTemplateWizardStepType, WizardStepProps } from '@/features/agents/types';

import { useGetVaultDetailQuery, useGetVaultsQuery } from '../api/coderApi';
import type { AttachSecretsValue, SecretReference, VaultSummaryView } from '../types';
import { isAttachSecretsValue } from '../types';

/** "3 keys across 2 vaults"; '' when nothing is attached (renders nothing). */
function summarizeReferences(references: SecretReference[]): string {
  if (references.length === 0) {
    return '';
  }
  const keys = references.reduce((sum, reference) => sum + reference.keys.length, 0);
  const vaults = references.length;
  return `${keys} ${keys === 1 ? 'key' : 'keys'} across ${vaults} ${vaults === 1 ? 'vault' : 'vaults'}`;
}

interface VaultRowProps {
  vault: VaultSummaryView;
  stepId: string;
  reference?: SecretReference;
  onSetReference: (reference: SecretReference) => void;
  onRemoveReference: (vaultId: string) => void;
}

/**
 * One vault row. A component per vault keeps the hook-per-instance rule for
 * the LAZY key fetch (§8.2): the detail query only runs once the vault is
 * checked; the key list shows unchecked by default (least privilege, G3).
 */
function VaultRow({ vault, stepId, reference, onSetReference, onRemoveReference }: VaultRowProps) {
  const [checked, setChecked] = useState(() => Boolean(reference));
  const [expanded, setExpanded] = useState(() => Boolean(reference));
  const disabled = vault.settings?.allowAgentRead === false;

  const detailQuery = useGetVaultDetailQuery(vault.id, { skip: !checked });
  const entries = useMemo(() => detailQuery.data?.entries ?? [], [detailQuery.data]);

  const vaultCheckboxId = `vault-${stepId}-${vault.id}`;
  const keysRegionId = `vault-keys-${stepId}-${vault.id}`;

  function handleVaultToggle() {
    if (disabled) {
      return;
    }
    const nextChecked = !checked;
    setChecked(nextChecked);
    setExpanded(nextChecked);
    if (!nextChecked) {
      // Unchecking collapses and clears the vault's keys (§8.2).
      onRemoveReference(vault.id);
    }
  }

  function handleKeyToggle(key: string) {
    const currentKeys = reference?.keys ?? [];
    const nextKeys = currentKeys.includes(key)
      ? currentKeys.filter((entry) => entry !== key)
      : [...currentKeys, key];
    if (nextKeys.length === 0) {
      // A vault with zero checked keys does not enter the draft (§8.2).
      onRemoveReference(vault.id);
    } else {
      onSetReference({ vaultId: vault.id, vaultName: vault.name, keys: nextKeys });
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex min-h-10 items-center gap-3">
        <Checkbox
          id={vaultCheckboxId}
          checked={checked}
          disabled={disabled}
          onCheckedChange={handleVaultToggle}
          aria-expanded={checked && expanded}
          aria-controls={keysRegionId}
        />
        <Label
          htmlFor={vaultCheckboxId}
          className={
            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer text-base font-medium'
          }
        >
          {vault.name}
        </Label>
        {disabled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Disabled checkboxes swallow pointer events; the span keeps hover alive. */}
              <span className="inline-flex items-center text-muted-foreground">
                <Lock className="h-4 w-4" aria-hidden="true" />
              </span>
            </TooltipTrigger>
            <TooltipContent>Agent reads are disabled for this vault.</TooltipContent>
          </Tooltip>
        ) : null}
      </div>

      {checked && expanded ? (
        <div id={keysRegionId} className="pl-7">
          {detailQuery.isLoading ? (
            <div className="space-y-2" aria-hidden="true">
              <div className="h-6 animate-pulse rounded-md bg-muted" />
              <div className="h-6 w-3/4 animate-pulse rounded-md bg-muted" />
            </div>
          ) : detailQuery.isError ? (
            <QueryErrorView
              error={detailQuery.error}
              label="vault keys"
              onRetry={() => void detailQuery.refetch()}
            />
          ) : entries.length === 0 ? (
            <p className="text-sm text-muted-foreground">This vault has no entries yet.</p>
          ) : (
            <fieldset className="space-y-1">
              <legend className="sr-only">{vault.name} keys</legend>
              {entries.map((entry, index) => {
                const keyCheckboxId = `${keysRegionId}-key-${index}`;
                return (
                  <div key={entry.key} className="flex min-h-10 items-center gap-2">
                    <Checkbox
                      id={keyCheckboxId}
                      checked={(reference?.keys ?? []).includes(entry.key)}
                      onCheckedChange={() => handleKeyToggle(entry.key)}
                    />
                    <Label htmlFor={keyCheckboxId} className="cursor-pointer font-mono text-xs">
                      {entry.key}
                    </Label>
                  </div>
                );
              })}
            </fieldset>
          )}
        </div>
      ) : null}
    </div>
  );
}

function AttachSecretsStep({ step, value, onChange }: WizardStepProps) {
  const vaultsQuery = useGetVaultsQuery();
  const draft = isAttachSecretsValue(value) ? value : { references: [], summary: '' };
  const vaults = useMemo(() => vaultsQuery.data ?? [], [vaultsQuery.data]);

  function setReference(reference: SecretReference) {
    const exists = draft.references.some((entry) => entry.vaultId === reference.vaultId);
    const next = exists
      ? draft.references
          .map((entry) => (entry.vaultId === reference.vaultId ? reference : entry))
          .filter((entry) => entry.keys.length > 0)
      : [...draft.references, reference];
    onChange({ references: next, summary: summarizeReferences(next) } satisfies AttachSecretsValue);
  }

  function removeReference(vaultId: string) {
    const next = draft.references.filter((entry) => entry.vaultId !== vaultId);
    onChange({ references: next, summary: summarizeReferences(next) } satisfies AttachSecretsValue);
  }

  if (vaultsQuery.isError) {
    return (
      <QueryErrorView
        error={vaultsQuery.error}
        label="vaults"
        onRetry={() => void vaultsQuery.refetch()}
      />
    );
  }

  const vaultsEmpty = !vaultsQuery.isLoading && vaults.length === 0;
  if (vaultsEmpty) {
    return (
      <EmptyState
        title="No vaults"
        description="Ask your administrator to create a vault, then come back."
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Secrets the agent can read at run time. Only attach what it needs.
      </p>

      {vaultsQuery.isLoading ? (
        <LoadingRows label="Loading vaults…" columns={2} rows={3} />
      ) : (
        <div className="space-y-3">
          {vaults.map((vault) => (
            <VaultRow
              key={vault.id}
              vault={vault}
              stepId={step.id}
              reference={draft.references.find((entry) => entry.vaultId === vault.id)}
              onSetReference={setReference}
              onRemoveReference={removeReference}
            />
          ))}
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        Secret values stay in the vault — the agent reads them only while it's running.
      </p>
    </div>
  );
}

export const type: AgentTemplateWizardStepType = 'attach-secrets';
export const component = AttachSecretsStep;
